import React, { useEffect, useState } from 'react';
import GoogleTranslate from './GoogleTranslate';
import Badge from './ui/Badge';
import Skeleton from './ui/Skeleton';

const CATEGORIES = {
  '[FEATURE]': { label: 'Nouveauté', tone: 'violet' },
  '[CLIENT]': { label: 'Client', tone: 'blue' },
  '[ADMIN]': { label: 'Admin', tone: 'amber' },
  '[IMPROVEMENT]': { label: 'Amélioration', tone: 'green' },
  '[DEBUG]': { label: 'Correctif', tone: 'red' },
};

// "[FEATURE] Text" → { badge, text }
const parseChange = (change) => {
  const entry = Object.entries(CATEGORIES).find(([code]) => change.startsWith(code));
  return entry ? { badge: entry[1], text: change.replace(entry[0], '').trim() } : { badge: null, text: change };
};

const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Release notes (public/changelog.json) as a timeline, newest first. */
function Changelog() {
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/changelog.json')
      .then((response) => response.json())
      .then((data) => setChangelog(data))
      .catch((error) => console.error('Erreur lors du chargement du changelog:', error))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-3" aria-label="Chargement des nouveautés">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!Array.isArray(changelog) || changelog.length === 0) return null;

  const releases = [...changelog].reverse();

  return (
    <div className="space-y-5">
      <ol className="relative space-y-6 border-l border-gray-200 pl-5 dark:border-gray-800">
        {releases.map((release, index) => (
          <li key={release.version} className="relative">
            <span
              className={`absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-white dark:ring-night-800 ${index === 0 ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              aria-hidden
            />
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">v{release.version}</span>
              <time dateTime={release.date} className="text-xs text-gray-500 dark:text-gray-400">{formatDate(release.date)}</time>
            </div>
            <ul className="mt-2 space-y-2">
              {release.changes.map((change, changeIndex) => {
                const { badge, text } = parseChange(change);
                return (
                  <li key={changeIndex} className="text-sm leading-snug text-gray-600 dark:text-gray-300">
                    {badge && <Badge tone={badge.tone} size="sm" className="mb-1 mr-1.5 align-middle">{badge.label}</Badge>}
                    {text}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4 text-xs text-gray-500 dark:border-gray-900 dark:text-gray-400">
        <span>Traduire les nouveautés</span>
        <GoogleTranslate />
      </div>
    </div>
  );
}

export default Changelog;
